import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  SelectChangeEvent,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Mock data for residents
const mockResidents = [
  { id: 1, name: 'John Smith', room: '201', admissionDate: '2023-01-15' },
  { id: 2, name: 'Mary Johnson', room: '202', admissionDate: '2023-02-10' },
  { id: 3, name: 'Robert Williams', room: '203', admissionDate: '2023-03-05' },
];

const hospiceAssessmentSections = [
  { id: 'patient_info', title: 'Patient Information', description: 'Basic patient demographics and insurance' },
  { id: 'diagnosis', title: 'Primary Diagnosis', description: 'Principal hospice diagnosis' },
  { id: 'secondary_diagnosis', title: 'Secondary Diagnoses', description: 'Additional diagnoses' },
  { id: 'symptoms', title: 'Current Symptoms', description: 'Patient symptoms and pain levels' },
  { id: 'comfort_measures', title: 'Comfort Measures', description: 'Pain management and comfort care' },
  { id: 'family_support', title: 'Family Support', description: 'Family involvement and support systems' },
  { id: 'spiritual_care', title: 'Spiritual Care', description: 'Spiritual needs and support' },
  { id: 'bereavement', title: 'Bereavement Needs', description: 'Family bereavement needs assessment' },
  { id: 'care_plan', title: 'Care Plan', description: 'Hospice care plan details' },
  { id: 'physician_order', title: 'Physician Orders', description: 'Medical orders and directives' },
];

const HospiceAssessmentPage: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedResident, setSelectedResident] = useState<number | null>(null);
  const [hospiceElectionDate, setHospiceElectionDate] = useState<Date | null>(null);
  const [physicianCertificationDate, setPhysicianCertificationDate] = useState<Date | null>(null);
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState('');
  const [prognosis, setPrognosis] = useState('');
  const [careLevel, setCareLevel] = useState('');
  const [assessmentData, setAssessmentData] = useState<Record<string, any>>({});
  
  const steps = [
    'Select Patient',
    'Hospice Details',
    'Assessment Sections',
    'Review & Submit'
  ];

  const handleResidentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedResident(Number(event.target.value));
  };

  const handleCareLevelChange = (event: SelectChangeEvent<string>) => {
    setCareLevel(event.target.value as string);
  };

  const handleDataChange = (sectionId: string, field: string, value: any) => {
    setAssessmentData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        [field]: value
      }
    }));
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0: // Select Patient
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Patient for Hospice Assessment
            </Typography>
            <RadioGroup value={selectedResident} onChange={handleResidentChange}>
              {mockResidents.map((resident) => (
                <FormControlLabel
                  key={resident.id}
                  value={resident.id}
                  control={<Radio />}
                  label={`${resident.name} (Room: ${resident.room}, Admitted: ${resident.admissionDate})`}
                />
              ))}
            </RadioGroup>
          </Box>
        );
      
      case 1: // Hospice Details
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Hospice Assessment Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Hospice Election Date"
                    value={hospiceElectionDate}
                    onChange={(newValue) => setHospiceElectionDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Physician Certification Date"
                    value={physicianCertificationDate}
                    onChange={(newValue) => setPhysicianCertificationDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Primary Diagnosis"
                  value={primaryDiagnosis}
                  onChange={(e) => setPrimaryDiagnosis(e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Prognosis (Months)"
                  value={prognosis}
                  onChange={(e) => setPrognosis(e.target.value)}
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Level of Care</InputLabel>
                  <Select
                    value={careLevel}
                    label="Level of Care"
                    onChange={handleCareLevelChange}
                  >
                    <MenuItem value="routine">Routine Home Care</MenuItem>
                    <MenuItem value="continuous">Continuous Home Care</MenuItem>
                    <MenuItem value="inpatient_respite">Inpatient Respite Care</MenuItem>
                    <MenuItem value="general_inpatient">General Inpatient Care</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );
      
      case 2: // Assessment Sections
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Hospice Assessment Sections
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Complete the required sections for the hospice comprehensive assessment.
            </Typography>
            
            {hospiceAssessmentSections.map((section) => (
              <Accordion key={section.id} defaultExpanded={section.id === 'patient_info'}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>{section.title}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {section.description}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Assessment Notes"
                    multiline
                    rows={3}
                    value={assessmentData[section.id]?.notes || ''}
                    onChange={(e) => handleDataChange(section.id, 'notes', e.target.value)}
                    placeholder="Enter assessment details here..."
                  />
                  <Box sx={{ mt: 1 }}>
                    <FormControlLabel 
                      control={
                        <Checkbox 
                          checked={assessmentData[section.id]?.completed || false}
                          onChange={(e) => handleDataChange(section.id, 'completed', e.target.checked)}
                        />
                      } 
                      label="Section Completed" 
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        );
      
      case 3: // Review & Submit
        const selectedResidentInfo = mockResidents.find(r => r.id === selectedResident);
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Hospice Assessment
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Please review the assessment details before submission.
            </Typography>
            
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Patient:</Typography>
                    <Typography>{selectedResidentInfo?.name}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Level of Care:</Typography>
                    <Typography>{careLevel}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Hospice Election Date:</Typography>
                    <Typography>{hospiceElectionDate?.toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2">Certification Date:</Typography>
                    <Typography>{physicianCertificationDate?.toLocaleDateString()}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Primary Diagnosis:</Typography>
                    <Typography>{primaryDiagnosis}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Prognosis:</Typography>
                    <Typography>{prognosis} months</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Sections Completed:</Typography>
                    <Typography>
                      {Object.keys(assessmentData).filter(
                        sectionId => assessmentData[sectionId].completed
                      ).length} of {hospiceAssessmentSections.length} sections
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            <Typography variant="body2" color="text.secondary">
              Hospice assessment will be used for Medicare billing and care planning.
            </Typography>
          </Box>
        );
      
      default:
        return <div>Unknown step</div>;
    }
  };

  const handleNext = () => {
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleSubmit = () => {
    // In a real app, this would submit to an API
    console.log('Submitting Hospice assessment:', {
      residentId: selectedResident,
      hospiceElectionDate,
      physicianCertificationDate,
      primaryDiagnosis,
      prognosis,
      careLevel,
      assessmentData
    });
    alert('Hospice assessment submitted successfully!');
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Hospice Assessment
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {getStepContent(activeStep)}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          {activeStep > 0 && (
            <Button onClick={handleBack}>Back</Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={activeStep === 0 && selectedResident === null}
              sx={{ ml: 'auto' }}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{ ml: 'auto' }}
            >
              Submit Assessment
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default HospiceAssessmentPage;